import _ from 'lodash';
import axios from 'axios';

import { coinDesc } from '@/api/const';
import { calculateBreakeven } from '@/api/utils';

function getDurationAlias(d) {
  const months = Math.round(d / 30);
  if (months <= 1) return '1 month';
  if (months < 12) return months + ' months';

  const years = Math.round(d / 365);
  if (years === 1) return '1 year';
  else return years + ' years';
}

export { getDurationAlias }
export default {
  async list() {
    const { data } = await axios.get('https://8xiscecoe2.execute-api.ap-northeast-1.amazonaws.com/production/hashpower/cloud_mining/list');
    const lastUpdate = Math.max(...data.map(v => v.update_time));

    return _.chain(data)
      .filter(v => v.issuers !== 'iqmining' && v.duration > 0 && v.sold_percent < 99.99 && lastUpdate - v.update_time < 3600)
      .map(v => {
        v.contract_cost = v.upfront_fee / v.contract_size / v.duration + v.electricity_fee * 1;
        v.honeyLemon_contract_name = `${v.coin} ${v.duration} Days`;
        return v;
      })
      .sortBy(['coin', 'issuers', 'duration', 'contract_cost', 'contract_size'])
      .sortedUniqBy(v => v.coin + v.issuers + v.duration + v.contract_cost.toFixed(5))
      .map(v => ({
        id: v.id,
        type: 'CLOUD_MINING',
        name:  v.issuers === 'bitdeer' && v.coin === 'BTC' ? v.honeyLemon_contract_name.replace('BTC', 'BTC/BCH') : v.honeyLemon_contract_name,
        contract_cost: v.contract_cost, // used for sort only
        issuer: v.issuers,
        payoffType: v.issuers === 'bitdeer' && v.coin === 'BTC' ? ['BTC', 'BCH'] : [v.coin],
        upfront: v.upfront_fee,
        size: v.contract_size * coinDesc[v.coin].unitFactor,
        elecFeeUnit: v.electricity_fee / coinDesc[v.coin].unitFactor,
        managementFeeRate: v.management_fee,
        duration: v.duration,
        durationAlias: getDurationAlias(v.duration),
        availablePercent: 1 - v.sold_percent / 100,
        url: v.buy_url
      }))
      .value();
  },
  getFullData(item, coin, blockchainInfo) {
    const blockchain = blockchainInfo[coin];
    const halvingTs = blockchain.halvingTs || 0;
    const now = Date.now();
    const preHalvingDays = Math.min(item.duration, halvingTs < now ? item.duration : (halvingTs - now) / 86400000);
    const postHalvingDays = item.duration - preHalvingDays;

    const managementFee = (blockchain.miningEarning * blockchain.price - item.elecFeeUnit) * item.managementFeeRate;
    const managementFeePostHalving = (blockchain.miningEarning * blockchain.price / 2 - item.elecFeeUnit) * item.managementFeeRate;

    const daily = {
      upfront: item.upfront / item.size / item.duration,
      elecFee: item.elecFeeUnit,
      preHalving: {
        payoff: blockchain.miningEarning * blockchain.price,
        managementFee: Math.max(managementFee, 0),
      },
      postHalving: {
        payoff: blockchain.miningEarning * blockchain.price / 2,
        managementFee: Math.max(managementFeePostHalving, 0),
      }
    };

    daily.preHalving.fee = Math.min(daily.preHalving.payoff, daily.elecFee + daily.preHalving.managementFee);
    daily.postHalving.fee = Math.min(daily.postHalving.payoff, daily.elecFee + daily.postHalving.managementFee);

    daily.preHalving.cost = daily.upfront + daily.preHalving.fee;
    daily.postHalving.cost = daily.upfront + daily.postHalving.fee;

    daily.preHalving.roi = daily.preHalving.payoff / daily.preHalving.cost - 1;
    daily.postHalving.roi = daily.postHalving.payoff / daily.postHalving.cost - 1;

    const total = {
      upfront: item.upfront,
      payoff: daily.preHalving.payoff * item.size * preHalvingDays + daily.postHalving.payoff * item.size * postHalvingDays,
      fee: daily.preHalving.fee * preHalvingDays * item.size + daily.postHalving.fee * postHalvingDays * item.size
    };

    total.cost = total.upfront + total.fee;
    total.roi = total.payoff / total.cost - 1;

    return {
      dealWithHalving: postHalvingDays > 0,
      ...item,
      coin,
      daily,
      total,
      breakevenDays: calculateBreakeven(item.upfront / item.size, daily.preHalving.payoff, daily.elecFee, blockchain.halvingTs || 0, item.duration),
      costBasis: total.cost / total.payoff * blockchain.price,
      discount: 1 - total.cost / total.payoff
    };
  }
}